import React, { useState, useEffect } from "react";
import { Button } from "./ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminPanel = () => {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState({
    contacts: [],
    services: [],
    events: [],
    testimonials: [],
    newsletters: []
  });

  useEffect(() => {
    if (activeTab !== 'dashboard') {
      loadData();
    } else {
      loadDashboardData();
    }
  }, [activeTab]);

  const loadDashboardData = async () => {
    setLoading(true);
    try {
      const [contacts, events, testimonials] = await Promise.all([
        axios.get(`${API}/contact/submissions?limit=5`).catch(() => ({ data: [] })),
        axios.get(`${API}/events/?limit=5`).catch(() => ({ data: [] })),
        axios.get(`${API}/testimonials/?approved_only=false&limit=5`).catch(() => ({ data: [] }))
      ]);

      setData(prev => ({
        ...prev,
        contacts: contacts.data,
        events: events.data,
        testimonials: testimonials.data
      }));
    } catch (error) {
      console.error('Error loading dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadData = async () => {
    setLoading(true);
    try {
      let response;
      switch (activeTab) {
        case 'contacts':
          response = await axios.get(`${API}/contact/submissions`);
          setData(prev => ({ ...prev, contacts: response.data }));
          break;
        case 'services':
          response = await axios.get(`${API}/services/`);
          setData(prev => ({ ...prev, services: response.data }));
          break;
        case 'events':
          response = await axios.get(`${API}/events/?active_only=false`);
          setData(prev => ({ ...prev, events: response.data }));
          break;
        case 'testimonials':
          response = await axios.get(`${API}/testimonials/?approved_only=false`);
          setData(prev => ({ ...prev, testimonials: response.data }));
          break;
        case 'newsletter':
          response = await axios.get(`${API}/newsletter/subscribers`);
          setData(prev => ({ ...prev, newsletters: response.data }));
          break;
        default:
          break;
      }
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const approveTestimonial = async (testimonialId) => {
    try {
      await axios.put(`${API}/testimonials/${testimonialId}/approve`);
      alert('Testimonial godkendt!');
      loadData();
    } catch (error) {
      alert('Fejl ved godkendelse');
    }
  };

  const toggleTestimonialFeatured = async (testimonialId, featured) => {
    try {
      await axios.put(`${API}/testimonials/${testimonialId}/feature?featured=${!featured}`);
      alert(featured ? 'Fjernet fra forside' : 'Tilføjet til forside');
      loadData();
    } catch (error) {
      alert('Fejl ved opdatering');
    }
  };

  const markContactResponded = async (contactId) => {
    try {
      await axios.put(`${API}/contact/submissions/${contactId}/respond`);
      alert('Markeret som besvaret!');
      loadData();
    } catch (error) {
      alert('Fejl ved opdatering');
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('da-DK', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const TabButton = ({ id, label, count }) => (
    <button
      onClick={() => setActiveTab(id)}
      className={`px-4 py-2 rounded-lg font-medium transition-colors ${
        activeTab === id
          ? 'bg-blue-600 text-white'
          : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
      }`}
    >
      {label} {count !== undefined && `(${count})`}
    </button>
  );

  return (
    <div className="min-h-screen bg-gray-100 p-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Stine Eskesen Coaching - Admin Panel
          </h1>
          <p className="text-gray-600">Administrer dit website nemt og enkelt</p>
        </div>

        {/* Navigation */}
        <div className="flex flex-wrap gap-2 mb-8">
          <TabButton id="dashboard" label="Dashboard" />
          <TabButton id="contacts" label="Henvendelser" count={data.contacts.length} />
          <TabButton id="services" label="Services" count={data.services.length} />
          <TabButton id="events" label="Events" count={data.events.length} />
          <TabButton id="testimonials" label="Anmeldelser" count={data.testimonials.length} />
          <TabButton id="newsletter" label="Newsletter" count={data.newsletters.length} />
        </div>

        {/* Content */}
        <div className="space-y-6">
          {/* Dashboard */}
          {activeTab === 'dashboard' && (
            <div className="grid md:grid-cols-3 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle className="text-blue-600">📧 Nye Henvendelser</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold mb-2">{data.contacts.length}</div>
                  <p className="text-gray-600 text-sm">Seneste henvendelser fra kontaktformularen</p>
                  {data.contacts.slice(0, 2).map(contact => (
                    <div key={contact.id} className="mt-3 p-2 bg-gray-50 rounded">
                      <div className="font-medium">{contact.name}</div>
                      <div className="text-sm text-gray-600">{contact.email}</div>
                    </div>
                  ))}
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-green-600">📅 Kommende Events</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold mb-2">{data.events.length}</div>
                  <p className="text-gray-600 text-sm">Aktive events på websitet</p>
                  {data.events.slice(0, 2).map(event => (
                    <div key={event.id} className="mt-3 p-2 bg-gray-50 rounded">
                      <div className="font-medium">{event.title}</div>
                      <div className="text-sm text-gray-600">{event.spots_left} pladser tilbage</div>
                    </div>
                  ))}
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-purple-600">⭐ Anmeldelser</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold mb-2">{data.testimonials.filter(t => !t.approved).length}</div>
                  <p className="text-gray-600 text-sm">Anmeldelser der venter på godkendelse</p>
                  {data.testimonials.filter(t => !t.approved).slice(0, 2).map(testimonial => (
                    <div key={testimonial.id} className="mt-3 p-2 bg-gray-50 rounded">
                      <div className="font-medium">{testimonial.client_name}</div>
                      <div className="text-sm text-gray-600">⭐ {testimonial.rating}/5</div>
                    </div>
                  ))}
                </CardContent>
              </Card>
            </div>
          )}

          {/* Contacts */}
          {activeTab === 'contacts' && (
            <div>
              <h2 className="text-2xl font-bold mb-4">📧 Henvendelser fra Kontaktformular</h2>
              {loading ? (
                <div className="text-center py-8">Indlæser...</div>
              ) : (
                <div className="space-y-4">
                  {data.contacts.map(contact => (
                    <Card key={contact.id} className={contact.responded ? 'bg-green-50' : 'bg-white'}>
                      <CardContent className="p-6">
                        <div className="flex justify-between items-start mb-4">
                          <div>
                            <h3 className="font-bold text-lg">{contact.name}</h3>
                            <p className="text-gray-600">{contact.email}</p>
                            {contact.phone && <p className="text-gray-600">{contact.phone}</p>}
                            <p className="text-sm text-gray-500">
                              Interesseret i: <span className="font-medium">{contact.service_interest}</span>
                            </p>
                            <p className="text-sm text-gray-500">
                              {formatDate(contact.created_at)}
                            </p>
                          </div>
                          <div className="flex gap-2">
                            {contact.responded ? (
                              <span className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm">
                                ✅ Besvaret
                              </span>
                            ) : (
                              <Button 
                                onClick={() => markContactResponded(contact.id)}
                                className="bg-blue-600 hover:bg-blue-700"
                                size="sm"
                              >
                                Marker som besvaret
                              </Button>
                            )}
                          </div>
                        </div>
                        <div className="bg-gray-50 p-4 rounded-lg">
                          <h4 className="font-medium mb-2">Besked:</h4>
                          <p className="text-gray-700 whitespace-pre-wrap">{contact.message}</p>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                  {data.contacts.length === 0 && (
                    <div className="text-center py-8 text-gray-500">
                      Ingen henvendelser endnu
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Services */}
          {activeTab === 'services' && (
            <div>
              <h2 className="text-2xl font-bold mb-4">💼 Coaching Services</h2>
              {loading ? (
                <div className="text-center py-8">Indlæser...</div>
              ) : (
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {data.services.map(service => (
                    <Card key={service.id} className={service.popular ? 'ring-2 ring-blue-500' : ''}>
                      <CardHeader>
                        <CardTitle className="flex justify-between items-start">
                          <span>{service.title}</span>
                          {service.popular && (
                            <span className="bg-blue-500 text-white px-2 py-1 rounded text-sm">
                              Populær
                            </span>
                          )}
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="mb-4">
                          <div className="text-2xl font-bold text-blue-600">
                            {service.price} {service.currency}
                          </div>
                          <div className="text-gray-600">{service.duration}</div>
                        </div>
                        <p className="text-gray-700 mb-4">{service.description}</p>
                        <div className="space-y-2">
                          <h4 className="font-medium">Features:</h4>
                          <ul className="text-sm text-gray-600 space-y-1">
                            {service.features.map((feature, index) => (
                              <li key={index}>• {feature}</li>
                            ))}
                          </ul>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Testimonials */}
          {activeTab === 'testimonials' && (
            <div>
              <h2 className="text-2xl font-bold mb-4">⭐ Klient Anmeldelser</h2>
              {loading ? (
                <div className="text-center py-8">Indlæser...</div>
              ) : (
                <div className="space-y-4">
                  {data.testimonials.map(testimonial => (
                    <Card key={testimonial.id} className={!testimonial.approved ? 'border-orange-300 bg-orange-50' : testimonial.featured ? 'border-green-300 bg-green-50' : ''}>
                      <CardContent className="p-6">
                        <div className="flex justify-between items-start mb-4">
                          <div>
                            <h3 className="font-bold text-lg">{testimonial.client_name}</h3>
                            <p className="text-gray-600">{testimonial.client_role}</p>
                            <div className="flex items-center mt-2">
                              <span className="text-yellow-500">
                                {'⭐'.repeat(testimonial.rating)}
                              </span>
                              <span className="ml-2 text-gray-600">({testimonial.rating}/5)</span>
                            </div>
                          </div>
                          <div className="flex gap-2 flex-wrap">
                            {!testimonial.approved && (
                              <Button 
                                onClick={() => approveTestimonial(testimonial.id)}
                                className="bg-green-600 hover:bg-green-700"
                                size="sm"
                              >
                                Godkend
                              </Button>
                            )}
                            {testimonial.approved && (
                              <Button 
                                onClick={() => toggleTestimonialFeatured(testimonial.id, testimonial.featured)}
                                className={testimonial.featured ? "bg-gray-600 hover:bg-gray-700" : "bg-blue-600 hover:bg-blue-700"}
                                size="sm"
                              >
                                {testimonial.featured ? 'Fjern fra forside' : 'Tilføj til forside'}
                              </Button>
                            )}
                          </div>
                        </div>
                        <div className="bg-white p-4 rounded-lg border">
                          <p className="text-gray-700 italic">"{testimonial.content}"</p>
                        </div>
                        <div className="mt-3 flex gap-2">
                          {!testimonial.approved && (
                            <span className="bg-orange-100 text-orange-800 px-3 py-1 rounded-full text-sm">
                              ⏳ Venter på godkendelse
                            </span>
                          )}
                          {testimonial.featured && (
                            <span className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm">
                              ⭐ Vist på forside
                            </span>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminPanel;